import {ChangeDetectionStrategy, Component, Injector, OnInit, Optional} from '@angular/core';
import {FormControl} from '@angular/forms';
import * as _ from 'lodash-es';
import {isObservable, merge, MonoTypeOperatorFunction, Observable, of} from 'rxjs';
import {debounceTime, filter, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../components/destroyable';
import {Widget} from '../../components/layout/widget/widget';
import {hasSettingUpdateField} from './common-widget.implementation';

export type WidgetSettingsType<WidgetType extends Widget> = WidgetType['implementationInstance']['settings'];

export interface BindControlToSettingsParams<WidgetType extends Widget,
    SettingsType = WidgetSettingsType<WidgetType>,
    Key extends keyof SettingsType = keyof SettingsType,
    SettingsFieldValue = SettingsType[Key],
    ControlValue = unknown> {
    widget: WidgetType;
    control: FormControl;
    settingsFieldName: Key;
    settingsFieldRead?: (settings: Partial<SettingsType>) => SettingsFieldValue;
    settingsFieldWrite?: (settings: Partial<SettingsType>, value: SettingsFieldValue) => void;
    controlValueEqualityPredicate?: (controlValue1: ControlValue, controlValue2: ControlValue) => boolean;
    onChange?: (settingsOrControlValue: SettingsFieldValue) => void;
    convertValueFromSettingsToControl?: (settingsValue: SettingsFieldValue) => ControlValue | Observable<ControlValue>;
    convertValueFromControlToSettings?: (controlValue: ControlValue) => SettingsFieldValue;
    controlToSettingsDebounceTime?: number;
}

export interface BindControlsToSettingsParams<WidgetType extends Widget,
    SettingsType = WidgetSettingsType<WidgetType>,
    Dict extends Partial<Record<keyof SettingsType, FormControl>> = Partial<Record<keyof SettingsType, FormControl>>> {
    widget: WidgetType;
    dict: Dict;
}

@Component({
    template:   '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})
export class WidgetImplSettingsComponentBase<WidgetType extends Widget> implements OnInit {

    public readonly destroy$ = destroy$();

    public readonly widget: WidgetType = this.injector && this.injector.get(Widget, undefined) as WidgetType;

    constructor(

        /**
         * Optional for backward compatibility.
         */
        @Optional()
        protected injector?: Injector
    ) {}

    public ngOnInit(): void {
        this.buildForm();
    }

    public bindControlToSettings<
        SettingsType extends WidgetSettingsType<WidgetType> = WidgetSettingsType<WidgetType>,
        Key extends keyof SettingsType = keyof SettingsType,
        SettingsFieldValue = SettingsType[Key],
        ControlValue = SettingsFieldValue>(
        params: Omit<BindControlToSettingsParams<WidgetType, SettingsType, Key, SettingsFieldValue, ControlValue>, 'widget'>
    ): void
    public bindControlToSettings<
        SettingsType extends WidgetSettingsType<WidgetType> = WidgetSettingsType<WidgetType>,
        Key extends keyof SettingsType = keyof SettingsType,
        SettingsFieldValue = SettingsType[Key],
        ControlValue = unknown>(
        params: Omit<BindControlToSettingsParams<WidgetType, SettingsType, Key, SettingsFieldValue, ControlValue>, 'widget'>
    ): void {
        bindControlToSettings({
            widget: this.widget,
            ...params
        }).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    protected buildForm(): void {
        // To be overridden.
    }

    protected createTitleFormControl<
        SettingsType extends WidgetSettingsType<WidgetType> = WidgetSettingsType<WidgetType>>(
        settings: SettingsType
    ): FormControl {
        const control: FormControl = new FormControl(settings.title);

        this.bindControlToSettings({control, settingsFieldName: 'title'});

        return control;
    }
}

export const bindControlToSettings = <WidgetType extends Widget,
    SettingsType extends WidgetSettingsType<WidgetType> = WidgetSettingsType<WidgetType>,
    Key extends keyof SettingsType = keyof SettingsType,
    SettingsFieldValue = SettingsType[Key],
    ControlValue = unknown>(
    {
        widget,
        control,
        settingsFieldName,
        settingsFieldRead,
        settingsFieldWrite,
        controlValueEqualityPredicate,
        onChange,
        convertValueFromSettingsToControl,
        convertValueFromControlToSettings,
        controlToSettingsDebounceTime
    }: BindControlToSettingsParams<WidgetType, SettingsType, Key, SettingsFieldValue, ControlValue>
): Observable<SettingsFieldValue | ControlValue> => {
    controlValueEqualityPredicate = controlValueEqualityPredicate
        || ((controlValue1, controlValue2) =>
            controlValue1 === controlValue2
                || _.isNil(controlValue1) && _.isNil(controlValue2) // null == undefined
        );

    const notifyChanged: MonoTypeOperatorFunction<SettingsFieldValue> = tap(value => {
        if (onChange) {
            onChange(value);
        }
    });

    const innerToControlValue: (settingsValue: SettingsFieldValue) => Observable<ControlValue> =
              convertValueFromSettingsToControl
                  ? (settingsValue => {
                      const result = convertValueFromSettingsToControl(settingsValue);

                      return isObservable(result) ? result : of(result);
                  })
                  : (settingsValue => of(settingsValue as unknown as ControlValue));

    convertValueFromControlToSettings = convertValueFromControlToSettings
        || (controlValue => controlValue as unknown as SettingsFieldValue);

    const controlToSettingsDebounce = <T>(): MonoTypeOperatorFunction<T> =>
        controlToSettingsDebounceTime
            ? debounceTime(controlToSettingsDebounceTime)
            : s$ => s$;

    const compatSettingsFieldRead: (settings: Partial<SettingsType>) => any =
        settingsFieldRead || (settings => settings[settingsFieldName]);
    const compatSettingsFieldWrite: (settings: Partial<SettingsType>, value: any) => void =
        settingsFieldWrite || ((settings, value) => settings[settingsFieldName] = value);
    const compatHasSettingUpdateField: (update: Partial<SettingsType>) => boolean =
              settingsFieldName
                ? update => hasSettingUpdateField(update, settingsFieldName)
                : update => compatSettingsFieldRead(update) !== undefined;

    const settingsUpdate$ = (widget.settingsWithUpdates$ as Observable<Partial<SettingsType>>).pipe(
        filter<Partial<SettingsType>>(update => compatHasSettingUpdateField(update)),
        map<Partial<SettingsType>, SettingsFieldValue>(compatSettingsFieldRead),
        switchMap(settingsValue => innerToControlValue(settingsValue).pipe(
            filter(newControlValue => !controlValueEqualityPredicate(newControlValue, control.value)),
            tap(newControlValue => control.reset(newControlValue)),
            map(() => settingsValue),
            notifyChanged
        ))
    );
    const controlUpdate$ = control.valueChanges.pipe(
        controlToSettingsDebounce(),
        switchMap(controlValue => innerToControlValue(
            compatSettingsFieldRead(widget.implementationInstance.settings as unknown as SettingsType)
        ).pipe(
            map(controlValueInSettings => ({
                controlValue,
                controlValueInSettings
            }))
        )),
        filter(({controlValue, controlValueInSettings}) =>
            !controlValueEqualityPredicate(
                controlValue,
                controlValueInSettings
            )
        ),
        map(({controlValue}) => convertValueFromControlToSettings(controlValue)),
        switchMap(settingsValue => {
            const update: Partial<SettingsType> = {};
            compatSettingsFieldWrite(update, settingsValue);

            return widget.implementationInstance.updateSettings(update).pipe(
                map(() => settingsValue)
            );
        }),
        notifyChanged
    );

    return merge(
        settingsUpdate$,
        controlUpdate$
    );
};

export const bindControlsToSettings = <WidgetType extends Widget,
    SettingsType extends WidgetSettingsType<WidgetType> = WidgetSettingsType<WidgetType>,
    Dict extends Partial<Record<keyof SettingsType, FormControl>> = Partial<Record<keyof SettingsType, FormControl>>>(
    {
        widget,
        dict
    }: BindControlsToSettingsParams<WidgetType, SettingsType, Dict>
): Observable<unknown> =>
    merge(
        ...(Object.keys(dict) as Array<keyof SettingsType>).map(settingsFieldName =>
            bindControlToSettings({
                widget,
                control:           dict[settingsFieldName],
                settingsFieldName: settingsFieldName as any
            })
        )
    );
